import type { FC } from 'react';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { Button, Col, Row, Select, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconPlus from '@/assets/icons/icon-plus.svg?react';
import Card from '@/components/RCard';
import PageHeader from '@/components/RPageHeader';
import SearchInput from '@/components/RSearchInput';
import UpdateTime from '@/components/RUpdateTime';
import { RUsageLimitIndicator } from '@/components/RUsageLimitIndicator';
import useAuth from '@/hooks/useAuth';
import useProductTour from '@/hooks/useProductTour';
import useStorageSystemsColumns from '@/hooks/useStorageSystemsColumns';
import { DrawerType } from '@/types/global';
import {
  QueryParamsFilterProps,
  StorageSystem,
  StorageSystemsResponse,
} from '@/types/storageSystem';
import { createQueryString, hasNonEmptyValues } from '@/utils';

import StorageCreateDrawer from './Drawer/StorageCreateDrawer';
import StorageGenericDrawer from './Drawer/StorageGenericDrawer';
import StyledStorageSystems from './styles';

const StorageSystems: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const [nameSearchValue, setNameSearchValue] = useState<string>();
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<
    'all' | 'active' | 'inactive'
  >('active');
  const [queryParams, setQueryParams] = useState<QueryParamsFilterProps>({
    name: '',
    isActive: undefined,
  });
  const queryString = createQueryString(queryParams);

  const storageSystemsCreateRef = useProductTour('createStorageSystem');

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState<boolean>(false);

  const deferredNameSearch = useDeferredValue(nameSearchValue);
  const [pageSize, setPageSize] = useState(7);
  const [offset, setOffset] = useState(1);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      name: deferredNameSearch || '',
      isActive: selectedActiveStatus,
    }));
  }, [deferredNameSearch, selectedActiveStatus]);

  const {
    data: storageSystemsData,
    isLoading,
    mutate,
  } = useSWR<StorageSystemsResponse>(
    hasNonEmptyValues(queryParams)
      ? `/storage/?limit=${pageSize}&offset=${
          (offset - 1) * pageSize
        }&${queryString}`
      : null
  );

  const [drawer, setDrawer] = useState<DrawerType<StorageSystem>>({
    type: null,
    data: null,
  });
  const updateDrawer = useCallback(
    (
      type: DrawerType<StorageSystem>['type'],
      data: DrawerType<StorageSystem>['data']
    ) => {
      setDrawer({ type, data });
    },
    []
  );

  const storageTableColumns = useStorageSystemsColumns(mutate, updateDrawer);
  const assetLimit = user?.organization.subscription.plan.simAssetLimit;

  const handlePaginationChange = (page: number) => {
    setOffset(page);
  };

  const handleCreateDrawerOpen = () => {
    setIsCreateDrawerOpen(true);
  };

  return (
    <StyledStorageSystems>
      <PageHeader
        title={t('storageSystem.pageTitle')}
        description={t('storageSystem.pageDescription')}
        rightSideSection={<RUsageLimitIndicator.Storages />}
      />
      <Card title={t('storageSystem.allStorageSystems.title')}>
        <Row
          justify="space-between"
          className="filter-header"
          style={{ padding: '22px' }}
        >
          <Col className="left-side">
            <Row>
              <Col>
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <SearchInput
                    placeholder={t(
                      'storageSystem.allStorageSystems.searchPlaceholder'
                    )}
                    onChange={(e) => setNameSearchValue(e.target.value)}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <Select
                    defaultValue={selectedActiveStatus}
                    placeholder={t(
                      'storageSystem.allStorageSystems.statusPlaceholder'
                    )}
                    style={{ width: 180 }}
                    onChange={(val) =>
                      setSelectedActiveStatus(
                        val as 'all' | 'active' | 'inactive'
                      )
                    }
                    options={[
                      {
                        value: 'all',
                        label: t(
                          'storageSystem.allStorageSystems.statusOptions.all'
                        ),
                      },
                      {
                        value: 'active',
                        label: t(
                          'storageSystem.allStorageSystems.statusOptions.active'
                        ),
                      },
                      {
                        value: 'inactive',
                        label: t(
                          'storageSystem.allStorageSystems.statusOptions.inactive'
                        ),
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <UpdateTime fetchFunction={mutate} />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col ref={storageSystemsCreateRef}>
            <Button
              type="primary"
              icon={<IconPlus fill={theme.colors.light} />}
              onClick={() => handleCreateDrawerOpen()}
              data-testid="create-storage-system-btn"
            >
              {t('storageSystem.allStorageSystems.create')}
            </Button>
          </Col>
        </Row>
        <Table
          rowKey="id"
          columns={storageTableColumns}
          dataSource={storageSystemsData?.results}
          loading={isLoading}
          size="small"
          pagination={{
            total: storageSystemsData?.count,
            pageSizeOptions: ['7', '10', '20', '50', '100'],
            pageSize: pageSize,
            hideOnSinglePage: true,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            onShowSizeChange: (current, size) => {
              setOffset(current);
              setPageSize(size);
            },
            showSizeChanger: true,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
      {assetLimit && storageSystemsData && isCreateDrawerOpen && (
        <StorageCreateDrawer
          mutate={mutate}
          open={isCreateDrawerOpen}
          setIsOpen={setIsCreateDrawerOpen}
          maxDataCount={assetLimit}
          currentDataCount={storageSystemsData?.count}
        />
      )}
      {assetLimit && storageSystemsData && drawer.type && (
        <StorageGenericDrawer
          mutate={mutate}
          drawerOptions={drawer}
          setDrawerOptions={setDrawer}
          maxDataCount={assetLimit}
          currentDataCount={storageSystemsData?.count}
        />
      )}
    </StyledStorageSystems>
  );
};

export default StorageSystems;
